import type { Toggle as BaseToggle } from '@base-ui/react/toggle'
import type { ToggleGroup as BaseToggleGroup } from '@base-ui/react/toggle-group'
import type { ComponentProps, ReactNode, Ref } from 'react'

/**
 * Toggle size variants
 */
export const ToggleSize = {
  /** 20px height - chip variant only (the default chip size) */
  XSmall: 'xs',
  /** 24px height */
  Small: 'small',
  /** 28px height */
  Medium: 'medium',
} as const

export type ToggleSize = (typeof ToggleSize)[keyof typeof ToggleSize]

/**
 * Toggle visual variants.
 *
 * - `default`: standard segmented-control / pill toggle (Small / Medium sizes).
 * - `chip`: chip that auto-widths to its content with a 20px minimum. Dashed
 *   border when inactive, solid border with a raised filled surface when active.
 *   Height is set by `size` (`xs` 20px default / `small` 24px / `medium` 28px).
 */
export type ToggleVariant = 'default' | 'chip'

/**
 * How a `default` ToggleGroup sizes its items.
 *
 * - `equal` (default): every item takes the same width.
 * - `content`: each item sizes to its label, sharing any spare width, so a
 *   long option ("Offer wanted") doesn't wrap beside short ones.
 */
export const ToggleItemWidth = {
  Equal: 'equal',
  Content: 'content',
} as const

export type ToggleItemWidth =
  (typeof ToggleItemWidth)[keyof typeof ToggleItemWidth]

type BaseToggleGroupSharedProps = Omit<
  ComponentProps<typeof BaseToggleGroup>,
  'children' | 'multiple' | 'value' | 'defaultValue' | 'onValueChange'
>

/**
 * Shared props between single and multiple ToggleGroup variants.
 */
interface ToggleGroupBaseProps extends BaseToggleGroupSharedProps {
  size?: ToggleSize
  /**
   * Visual variant, propagated to child Toggles via context (the same way
   * `size` is). `variant="chip"` also restyles the group container: no shared
   * background and a 4px gap between independent chips. Pass `aria-label` on
   * every child Toggle in a chip group - the inherited variant is not visible
   * to the type system, so `aria-label` cannot be type-enforced there.
   */
  variant?: ToggleVariant
  /** Item sizing for the `default` variant; chips always size to content. */
  itemWidth?: ToggleItemWidth
  className?: string
  children: ReactNode
  ref?: Ref<HTMLDivElement>
}

/**
 * Props for ToggleGroup in single-selection mode (default).
 *
 * Enforces "always exactly one selected": when the user attempts to deselect
 * the active toggle, `onValueChange` is not called and the value does not
 * change. Consumers do not need to guard against an empty value.
 */
export interface ToggleGroupSingleProps extends ToggleGroupBaseProps {
  type?: 'single'
  /**
   * Controlled value. When provided, the component is controlled and internal
   * state is not used. `defaultValue` is ignored in controlled mode.
   */
  value?: string
  /**
   * Uncontrolled initial value. Must be a non-empty string matching one of the
   * child Toggle `value`s. The "always exactly one selected" guarantee only
   * applies after the first user interaction, so passing `""` (or a value that
   * doesn't match any child) renders with nothing pressed on initial mount.
   */
  defaultValue?: string
  /**
   * Called when the user selects a different toggle. Not called when the user
   * attempts to deselect the active toggle.
   */
  onValueChange?: (value: string) => void
}

/**
 * Props for ToggleGroup in multiple-selection mode.
 *
 * Allows zero or more selected toggles. Empty selection is a valid state.
 */
export interface ToggleGroupMultipleProps extends ToggleGroupBaseProps {
  type: 'multiple'
  /**
   * Controlled value. When provided, the component is controlled and internal
   * state is not used. `defaultValue` is ignored in controlled mode.
   */
  value?: string[]
  /**
   * Uncontrolled initial value.
   */
  defaultValue?: string[]
  /**
   * Called when the user changes the selection.
   */
  onValueChange?: (value: string[]) => void
}

/**
 * Props for the ToggleGroup container. Discriminated by `type` (defaults to
 * `'single'`).
 */
export type ToggleGroupProps = ToggleGroupSingleProps | ToggleGroupMultipleProps

/**
 * Props shared between the default and chip Toggle variants. `aria-label` is
 * omitted here so each variant can redeclare it with its own optionality.
 */
interface ToggleSharedProps
  extends Omit<
    ComponentProps<typeof BaseToggle>,
    'pressed' | 'defaultPressed' | 'aria-label'
  > {
  /** Unique value identifying this item within the group */
  value?: string
  /** Item content (text, letter, or icon). SVG-only content auto-renders as square when standalone. */
  children: ReactNode
  className?: string
  ref?: Ref<HTMLButtonElement>
}

/**
 * Props for the default Toggle variant.
 *
 * @public — default Toggle variant props
 */
export interface ToggleDefaultProps extends ToggleSharedProps {
  variant?: 'default'
  /** Size variant (only used when standalone, group provides size via context). `xs` is chip-only. */
  size?: Exclude<ToggleSize, 'xs'>
  /** Accessible name. Optional - visible text content is usually a sufficient name. */
  'aria-label'?: string
}

/**
 * Props for the chip Toggle variant.
 *
 * A chip auto-widths to its content (20px minimum). The visible label is often
 * a 1-2 character abbreviation that is not a sufficient accessible name, so
 * `aria-label` is required.
 *
 * @public — chip Toggle variant props
 */
export interface ToggleChipProps extends ToggleSharedProps {
  variant: 'chip'
  /** Height only (chips auto-width). `xs` 20px (default) / `small` 24px / `medium` 28px. */
  size?: ToggleSize
  /** Accessible name. Required - the visible label is not a sufficient name. */
  'aria-label': string
}

/**
 * Props for Toggle (works both standalone and within a group). Discriminated by
 * `variant`: an explicit `variant="chip"` requires `aria-label` and disallows
 * `size`. A chip variant inherited from a `ToggleGroup` via context is not
 * visible to the type system, so `aria-label` is not type-enforced in that case
 * - pass it on every chip regardless.
 */
export type ToggleProps = ToggleDefaultProps | ToggleChipProps
